import React, { Fragment } from 'react';
import without from 'lodash/without';
import pt from 'prop-types';

import { webhookEventOptions } from 'constants/webhooks';

import { fetchEventModels } from 'services/event-models';

import { classNames, inflect } from 'utils';
import {
  buildWebhookModelEventOptions,
  formatWebhookEvent,
} from 'utils/events';

import Field from './field';

// Event roots of the standard list (`order` from `order.paid`)
const STANDARD_ROOTS = [
  ...new Set(
    webhookEventOptions.flatMap((group) =>
      group.options.map((option) => option.split('.')[0]),
    ),
  ),
];

export default class FieldEvents extends React.Component {
  static propTypes = {
    value: pt.any,
    defaultValue: pt.any,
    className: pt.string,
    // List events of the store's custom models and app models
    allEvents: pt.bool,
  };

  static contextTypes = {
    client: pt.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      value: this.getInitialValue(props),
      // Kept in state so the select gets the same array on every render
      options: webhookEventOptions,
    };

    this.mounted = false;
  }

  componentDidMount() {
    this.mounted = true;

    if (this.props.allEvents) {
      fetchEventModels().then((models) => {
        if (!this.mounted) {
          return;
        }

        const { custom, apps } = buildWebhookModelEventOptions(
          models,
          this.context.client?.appsById,
          STANDARD_ROOTS,
        );

        this.setState({
          options: [...webhookEventOptions, ...custom, ...apps],
        });
      });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidUpdate(prevProps) {
    const isValueChanged =
      prevProps.value !== this.props.value ||
      prevProps.defaultValue !== this.props.defaultValue;

    if (isValueChanged) {
      this.setState({ value: this.getInitialValue(this.props) });
    }
  }

  getInitialValue(props) {
    if (props.value !== undefined) {
      return (props.value instanceof Array ? props.value : []).sort();
    }

    return (
      props.defaultValue instanceof Array ? props.defaultValue : []
    ).sort();
  }

  // Same as the webhooks list: app IDs in model paths shown as app slugs
  getValueLabel(val) {
    return formatWebhookEvent(val, this.context.client?.appsById);
  }

  setValue(value) {
    this.setState({ value: (value || []).sort() });
  }

  onChangeEvents = (event, value) => {
    this.setValue(value);
  };

  onClickRemove = (event) => {
    event.preventDefault();
    const { val } = event.currentTarget.dataset;
    this.setValue(without(this.state.value, val));
  };

  onClickRemoveAll = (event) => {
    event.preventDefault();
    this.setValue([]);
  };

  onClickReceiveAll = (event) => {
    event.preventDefault();

    this.setValue(
      this.state.options.reduce((acc, group) => {
        acc.push(
          ...group.options.map((option) =>
            typeof option === 'object' ? option.value : option,
          ),
        );
        return acc;
      }, []),
    );
  };

  render() {
    const { value, className, allEvents, ...props } = this.props;

    const { value: stateValue } = this.state;

    return (
      <div className={classNames('form-events', className)}>
        <Field
          type="select"
          label="Events"
          placeholder="Select events"
          multiSelectable={true}
          groupSelectable={true}
          groupSelectLabel="events"
          {...props}
          defaultValue={stateValue}
          options={this.state.options}
          onChange={this.onChangeEvents}
        />

        <ul className="form-events-list">
          {stateValue && stateValue.length > 0 ? (
            stateValue.map((val) => (
              <li className="form-events-list-value" key={val}>
                {this.getValueLabel(val)}
                <button
                  data-val={val}
                  className="as-link remove"
                  onClick={this.onClickRemove}
                  type="button"
                >
                  &times;
                </button>
              </li>
            ))
          ) : (
            <div className="form-events-list-empty">
              <p>
                <b>No events selected</b>
                <br />
                Select events from the dropdown above,
                <br /> or{' '}
                <button
                  className="as-link"
                  onClick={this.onClickReceiveAll}
                  type="button"
                >
                  receive all events
                </button>
                .
              </p>
            </div>
          )}
        </ul>

        <footer className="form-events-list-foot">
          {stateValue && stateValue.length > 0 ? (
            <Fragment>
              {inflect(stateValue.length, 'events')} selected
              <button
                className="as-link remove-all"
                onClick={this.onClickRemoveAll}
                type="button"
              >
                Clear all
              </button>
            </Fragment>
          ) : (
            <Fragment>&nbsp;</Fragment>
          )}
        </footer>
      </div>
    );
  }
}
